import cn from "classnames";
import { paymentFieldsConfig } from "components/distributionBids/paymentFields";
import { Datetime, Field } from "components/shared/form";
import { bidPaymentStatusesEnum } from "helpers/enums";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Popover } from "react-tiny-popover";
import { applyReportPayment, paymentOverdueState } from "./payment";

const configs = {
    invoiceDate: {
        label: "Дата выстав. счета",
        inputProps: { name: "invoiceDate" },
    },
    planPaymentDate: {
        label: "План. дата оплаты",
        inputProps: { name: "planPaymentDate" },
    },
    factPaymentDate: {
        label: "Факт. дата оплаты",
        inputProps: { name: "factPaymentDate" },
    },
    comment: {
        label: "Комментарий",
        inputProps: { name: "comment", className: "form-control form-control-xs" },
    },
};

export default function WithChangeDate({ payment: initialPayment, name, updateData, isAccountsPayable }) {
    const [opened, setOpened] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [payment, setPayment] = useState(initialPayment ?? {});
    useEffect(() => {
        setPayment(initialPayment ?? {});
    }, [initialPayment]);

    const dateConfig = useMemo(
        () => paymentFieldsConfig(isAccountsPayable)[name] || configs[name],
        [isAccountsPayable, name],
    );

    const handleChange = useCallback(({ name, value }) => {
        if (["planPaymentDate", "invoiceDate", "factPaymentDate"].includes(name) && value) {
            value = moment(value).format("YYYY-MM-DD");
        }
        setPayment((prev) => ({ ...prev, [name]: value }));
        setError("");
    }, []);

    const savePayment = useCallback(() => {
        if (payment.bidPaymentStatus !== bidPaymentStatusesEnum.NotPaid && !payment.planPaymentDate) {
            setError(`Укажите ${paymentFieldsConfig(isAccountsPayable).planPaymentDate.label.toLowerCase()}`);
            return;
        }
        if (payment.bidPaymentStatus === bidPaymentStatusesEnum.Paid && !payment.factPaymentDate) {
            setError(`Укажите ${paymentFieldsConfig(isAccountsPayable).factPaymentDate.label.toLowerCase()}`);
            return;
        }
        if (
            initialPayment?.planPaymentDate &&
            payment.planPaymentDate !== initialPayment.planPaymentDate &&
            !payment.comment
        ) {
            setError("Укажите комментарий при изменении плановой даты");
            return;
        }
        setLoading(true);
        const {
            invoiceDate,
            planPaymentDate,
            factPaymentDate,
            bidPaymentStatus: paymentStatus,
            comment,
            remainingPayment,
        } = payment;
        const newPaymentData = {
            comment,
            paymentStatus,
            invoiceDate,
            planPaymentDate,
            factPaymentDate,
            remainingPayment,
        };
        applyReportPayment(payment, newPaymentData, isAccountsPayable).then((response) => {
            if (response) {
                setOpened(false);
                updateData({
                    ...payment,
                    ...newPaymentData,
                    bidPaymentStatus: paymentStatus,
                    ...paymentOverdueState(newPaymentData),
                });
            }
            setLoading(false);
        });
    }, [initialPayment?.planPaymentDate, isAccountsPayable, payment, updateData]);

    const handlePopoverToggle = useCallback(() => setOpened((open) => !open), []);

    const handlePopoverClose = useCallback(() => {
        setOpened(false);
        setPayment(initialPayment);
        setError("");
    }, [initialPayment]);

    const formattedValue = useMemo(() => {
        if (!payment[name]) {
            return "";
        }
        return name === "comment" ? payment[name] : moment(payment[name], "YYYY-MM-DD").format("DD.MM.YYYY");
    }, [payment, name]);

    const popoverContent = (
        <div className={cn("popover w-auto font-size-14", { "loading loading--small": loading })}>
            <div className="font-size-16 pb-2">Изменение данных об оплате</div>
            <div>
                {name === "comment" ? (
                    <Field
                        labelWrapper="col-12 small mb-0"
                        fieldWrapper="col-12"
                        {...configs.comment}
                        onChange={handleChange}
                        value={payment.comment ?? ""}
                    />
                ) : (
                    <>
                        <Datetime
                            labelWrapper="col-12 small mb-0"
                            fieldWrapper="col-12"
                            inPortal
                            label={dateConfig.label}
                            inputProps={{
                                name: dateConfig.inputProps.name,
                                type: "date",
                            }}
                            libOptions={{ className: "form-control form-control-xs" }}
                            onChange={handleChange}
                            value={payment[name]}
                        />
                        <Field
                            labelWrapper="col-12 small mb-0"
                            fieldWrapper="col-12"
                            {...configs.comment}
                            onChange={handleChange}
                            value={payment.comment ?? ""}
                        />
                    </>
                )}
                {error && <div className="text-danger small mb-2">{error}</div>}
                <div className="d-flex justify-content-between mb-2">
                    <button type="button" className="btn btn-xs btn-secondary" onClick={handlePopoverClose}>
                        Отмена
                    </button>
                    <button type="button" className="btn btn-xs btn-brand" onClick={savePayment}>
                        Сохранить
                    </button>
                </div>
            </div>
        </div>
    );

    const onClickOutside = useCallback(
        (e) => {
            if (opened && !e.target.closest(".react-datepicker")) {
                handlePopoverClose();
            }
        },
        [opened, handlePopoverClose],
    );

    return (
        <Popover
            isOpen={opened}
            content={popoverContent}
            containerStyle={{ zIndex: 130 }}
            onClickOutside={onClickOutside}
        >
            <button
                type="button"
                className="r-item__with-date border-0 bg-transparent text-reset"
                onClick={handlePopoverToggle}
            >
                <div className="flex-grow-1 text-center">
                    <div className={name === "comment" ? "text-wrap-auto two-line-ellipsis" : undefined}>
                        {formattedValue}
                    </div>
                </div>
                <i className="icon-uEA10-pen px-2" />
            </button>
        </Popover>
    );
}
